/** Receipt (80 mm thermal) and A4 GST tax invoice, as printable HTML. */
import type { ReceiptData } from '../types';
import { formatINR, formatQty } from './money';
import { escapeHtml as e } from './print';

const MODE_LABEL: Record<string, string> = { cash: 'Cash', card: 'Card', upi: 'UPI', credit: 'Credit' };

function dt(iso: string) {
  return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function licenceLine(r: ReceiptData) {
  const label = r.shop.settings?.license_label;
  return r.shop.license_no ? `${e(label || 'Licence')}: ${e(r.shop.license_no)}` : '';
}

export function receiptHtml(r: ReceiptData, widthMm = 80): string {
  const b = r.bill;
  const s = r.shop.settings || {};
  const rows = b.items.map((i) => `
    <tr><td colspan="3" class="nm">${e(i.name)}${i.size ? ` <span class="sz">${e(i.size)}</span>` : ''}</td></tr>
    <tr><td class="q">${formatQty(i.qty)} × ${formatINR(i.unit_price_paise).replace('₹', '')}</td>
        <td class="r">${i.line_discount_paise ? `-${formatINR(i.line_discount_paise)}` : ''}</td>
        <td class="r">${formatINR(i.gross_paise - i.line_discount_paise)}</td></tr>`).join('');
  const taxRows = r.tax_summary.map((t) => `<tr><td>GST ${t.gst_percent}%${t.hsn ? ` (${e(t.hsn)})` : ''}</td>
    <td class="r">CGST ${formatINR(t.cgst_paise)}</td><td class="r">SGST ${formatINR(t.sgst_paise)}</td></tr>`).join('');
  const pays = b.payments.map((p) => `<tr><td>${MODE_LABEL[p.mode] || e(p.mode)}${p.reference ? ` <span class="sz">${e(p.reference)}</span>` : ''}</td>
    <td class="r" colspan="2">${formatINR(p.amount_paise)}</td></tr>`).join('');
  const cash = b.payments.find((p) => p.mode === 'cash');
  const discount = b.line_discount_paise + b.bill_discount_paise;
  return `<!doctype html><html><head><meta charset="utf-8"><title>${e(b.bill_number)}</title><style>
    @page { size: ${widthMm}mm auto; margin: 0; }
    * { box-sizing: border-box; }
    body { margin: 0; padding: 3mm 3mm 6mm; width: ${widthMm}mm; font: 11px/1.35 'Segoe UI', Arial, sans-serif; color: #000; }
    h1 { font-size: 15px; margin: 0; text-align: center; }
    .c { text-align: center; } .r { text-align: right; } .sz { color: #333; font-size: 10px; }
    table { width: 100%; border-collapse: collapse; } td { vertical-align: top; padding: 0; }
    .nm { padding-top: 3px; font-weight: 600; } .q { white-space: nowrap; }
    hr { border: 0; border-top: 1px dashed #000; margin: 4px 0; }
    .tot td { font-size: 15px; font-weight: 700; padding: 2px 0; }
    .void { text-align: center; font-size: 22px; font-weight: 800; border: 2px solid #000; margin: 4px 0; }
  </style></head><body>
    <h1>${e(r.shop.name)}</h1>
    <div class="c">${e(r.shop.address || '')}${r.shop.phone ? `<br>Ph: ${e(r.shop.phone)}` : ''}</div>
    ${licenceLine(r) ? `<div class="c">${licenceLine(r)}</div>` : ''}
    ${r.shop.gstin ? `<div class="c">GSTIN: ${e(r.shop.gstin)}</div>` : ''}
    ${s.receipt_header ? `<div class="c">${e(s.receipt_header)}</div>` : ''}
    ${b.status === 'voided' ? '<div class="void">VOIDED</div>' : ''}
    <hr>
    <table><tr><td>Bill: <b>${e(b.bill_number)}</b></td><td class="r">${dt(b.created_at)}</td></tr>
      <tr><td>${e(b.counter_name || '')}</td><td class="r">By: ${e(b.cashier_name || '')}</td></tr>
      ${b.customer_name ? `<tr><td colspan="2">Customer: ${e(b.customer_name)} ${e(b.customer_phone || '')}</td></tr>` : ''}</table>
    <hr><table>${rows}</table><hr>
    <table>
      <tr><td>Items</td><td class="r" colspan="2">${b.items.length} (qty ${formatQty(b.items.reduce((a, i) => a + i.qty, 0))})</td></tr>
      ${discount ? `<tr><td>Discount</td><td class="r" colspan="2">-${formatINR(discount)}</td></tr>` : ''}
      <tr class="tot"><td>TOTAL</td><td class="r" colspan="2">${formatINR(b.total_paise)}</td></tr>
      ${b.tax_paise ? taxRows : ''}
      ${b.tax_paise ? `<tr><td colspan="3" class="sz">Prices are inclusive of all taxes</td></tr>` : ''}
    </table><hr>
    <table>${pays}
      ${cash?.tendered_paise ? `<tr><td>Cash received</td><td class="r" colspan="2">${formatINR(cash.tendered_paise)}</td></tr>` : ''}
      ${b.change_paise ? `<tr><td><b>Change</b></td><td class="r" colspan="2"><b>${formatINR(b.change_paise)}</b></td></tr>` : ''}
    </table><hr>
    <div class="c">${e(s.receipt_footer || 'Thank you! Visit again.')}</div>
  </body></html>`;
}

/** A4 GST tax invoice (when GST invoicing is on). */
export function invoiceA4Html(r: ReceiptData): string {
  const b = r.bill;
  const rows = b.items.map((i, n) => {
    const taxable = i.net_paise - i.tax_paise;
    return `<tr><td>${n + 1}</td><td>${e(i.name)} ${e(i.size || '')}</td><td>${e(i.hsn || '')}</td>
      <td class="r">${formatQty(i.qty)}</td><td class="r">${formatINR(i.unit_price_paise)}</td>
      <td class="r">${formatINR(i.line_discount_paise + i.bill_discount_share_paise)}</td>
      <td class="r">${formatINR(taxable)}</td><td class="r">${i.gst_percent}%</td>
      <td class="r">${formatINR(i.tax_paise - Math.floor(i.tax_paise / 2))}</td>
      <td class="r">${formatINR(Math.floor(i.tax_paise / 2))}</td><td class="r">${formatINR(i.net_paise)}</td></tr>`;
  }).join('');
  const taxRows = r.tax_summary.map((t) => `<tr><td>${t.gst_percent}%</td><td>${e(t.hsn || '')}</td>
    <td class="r">${formatINR(t.taxable_paise)}</td><td class="r">${formatINR(t.cgst_paise)}</td>
    <td class="r">${formatINR(t.sgst_paise)}</td><td class="r">${formatINR(t.tax_paise)}</td></tr>`).join('');
  return `<!doctype html><html><head><meta charset="utf-8"><title>Invoice ${e(b.bill_number)}</title><style>
    @page { size: A4; margin: 12mm; }
    body { font: 12px/1.4 'Segoe UI', Arial, sans-serif; color: #000; }
    h1 { margin: 0; font-size: 20px; } h2 { text-align: center; font-size: 15px; margin: 8px 0; letter-spacing: 1px; }
    table { width: 100%; border-collapse: collapse; margin-top: 6px; }
    th, td { border: 1px solid #555; padding: 4px 5px; } th { background: #f0f0f0; } .r { text-align: right; }
    .head td { border: 0; padding: 0; vertical-align: top; } .tot { font-weight: 700; font-size: 14px; }
  </style></head><body>
    <table class="head"><tr><td><h1>${e(r.shop.name)}</h1>${e(r.shop.address || '')}<br>Ph: ${e(r.shop.phone || '')}
      ${r.shop.gstin ? `<br><b>GSTIN: ${e(r.shop.gstin)}</b>` : ''}${licenceLine(r) ? `<br>${licenceLine(r)}` : ''}</td>
      <td class="r">Invoice No: <b>${e(b.bill_number)}</b><br>Date: ${dt(b.created_at)}<br>${e(b.counter_name || '')}</td></tr></table>
    <h2>TAX INVOICE${b.status === 'voided' ? ' — VOIDED' : ''}</h2>
    ${b.customer_name || b.customer_gstin ? `<div>Bill to: <b>${e(b.customer_name || '')}</b> ${e(b.customer_phone || '')}
      ${b.customer_gstin ? `<br>GSTIN: ${e(b.customer_gstin)}` : ''}</div>` : ''}
    <table><thead><tr><th>#</th><th>Item</th><th>HSN</th><th>Qty</th><th>Rate</th><th>Disc.</th><th>Taxable</th>
      <th>GST</th><th>CGST</th><th>SGST</th><th>Amount</th></tr></thead><tbody>${rows}</tbody>
      <tfoot><tr class="tot"><td colspan="10" class="r">Total (inclusive of GST)</td><td class="r">${formatINR(b.total_paise)}</td></tr></tfoot></table>
    <table><thead><tr><th>GST rate</th><th>HSN</th><th>Taxable value</th><th>CGST</th><th>SGST</th><th>Total tax</th></tr></thead>
      <tbody>${taxRows}</tbody></table>
    <p>Payment: ${b.payments.map((p) => `${MODE_LABEL[p.mode] || p.mode} ${formatINR(p.amount_paise)}${p.reference ? ` (${e(p.reference)})` : ''}`).join(', ')}</p>
    <p style="margin-top:40px" class="r">For ${e(r.shop.name)}<br><br>Authorised signatory</p>
  </body></html>`;
}
